import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Send, ArrowLeft, Loader2, FileDown, ChevronDown, ChevronUp, Info, AlertCircle, FileSpreadsheet, Upload, HelpCircle, Lock, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default empty question template (6 areas, 5 questions each)
const DEFAULT_AREAS = [
  "Personale",
  "Terze Parti", 
  "Misure Organizzative",
  "Sicurezza Fisica",
  "ICT",
  "Incidenti di Sicurezza"
];

// Predefined questions for the questionnaire
const PREDEFINED_QUESTIONS = [
  // Area: Personale (1.01 - 1.05)
  {
    question_id: "1",
    area: "Personale",
    text: "Vengono pianificati ed effettuati interventi formativi o campagne di consapevolezza periodiche (almeno annuali) sulla sicurezza delle informazioni e dati personali per tutto il personale?"
  },
  {
    question_id: "2",
    area: "Personale",
    text: "Sono definiti e applicati processi di screening/background check del personale prima dell'assunzione (Es. impieghi precedenti, titoli di studio, ecc.)?"
  },
  {
    question_id: "3",
    area: "Personale",
    text: "Tutto il personale è obbligato a sottoscrivere accordi di non divulgazione (NDA) o clausole di riservatezza al momento dell'assunzione?"
  },
  {
    question_id: "4",
    area: "Personale",
    text: "Esistono procedure formalizzate per la gestione dell'offboarding (cessazione del rapporto), inclusa la revoca tempestiva degli accessi e la restituzione dei beni aziendali?"
  },
  {
    question_id: "5",
    area: "Personale",
    text: "Sono state formalmente assegnate le responsabilità relative alla sicurezza delle informazioni e dati personali (es. Nomina Amministratori di Sistema, Incaricati al trattamento, Resp.le Sicurezza informatica, ecc.)?"
  },
  // Area: Terze Parti (2.01 - 2.05)
  {
    question_id: "6",
    area: "Terze Parti",
    text: "Esiste un processo per identificare i fornitori critici e valutare il rischio associato alle loro forniture?"
  },
  {
    question_id: "7",
    area: "Terze Parti",
    text: "Le terze parti rilevanti sono coinvolte negli interventi formativi o nelle simulazioni di risposta agli incidenti?"
  },
  {
    question_id: "8",
    area: "Terze Parti",
    text: "Nei contratti con i sub-fornitori sono incluse clausole di sicurezza e la propagazione degli obblighi di sicurezza lungo tutta la filiera?"
  },
  {
    question_id: "9",
    area: "Terze Parti",
    text: "Viene verificato periodicamente il rispetto delle clausole di sicurezza incluse nei contratti con le terze parti (es. tramite audit o report)?"
  },
  {
    question_id: "10",
    area: "Terze Parti",
    text: "Il fornitore assicura che i dati siano trattati e conservati esclusivamente all'interno del territorio dell'Unione Europea (o secondo i vincoli normativi applicabili)?"
  },
  // Area: Misure Organizzative (3.01 - 3.05)
  {
    question_id: "11",
    area: "Misure Organizzative",
    text: "Esiste una politica della sicurezza delle informazioni e dati personali, approvata dalla direzione e disponibile a tutte le parti interessate?"
  },
  {
    question_id: "12",
    area: "Misure Organizzative",
    text: "Viene mantenuto un inventario aggiornato dei beni aziendali (hardware, software, servizi e dati) con l'indicazione dei relativi responsabili?"
  },
  {
    question_id: "13",
    area: "Misure Organizzative",
    text: "Viene effettuata una valutazione del rischio (Risk Assessment) periodica che identifichi minacce, vulnerabilità e impatti sui processi critici?"
  },
  {
    question_id: "14",
    area: "Misure Organizzative",
    text: "Esiste un programma di audit interno o verifiche indipendenti, svolte con periodicità almeno annuale, per valutare la conformità ai requisiti per la sicurezza delle informazioni e dati personali?"
  },
  {
    question_id: "15",
    area: "Misure Organizzative",
    text: "È definito e allocato un budget specifico annuale per la cybersecurity in modo da indirizzare i rischi più elevati?"
  },
  // Area: Sicurezza Fisica (4.01 - 4.05)
  {
    question_id: "16",
    area: "Sicurezza Fisica",
    text: "Sono state identificate le Aree Critiche nelle quali sono gestite le informazioni o dati personali da proteggere (es. locali server, archivio fascicoli, ecc.)?"
  },
  {
    question_id: "17",
    area: "Sicurezza Fisica",
    text: "Per le Aree Critiche sono definite misure di protezione da accessi non autorizzati (serratura con chiave meccanica, autenticazione con badge, ecc.)?"
  },
  {
    question_id: "18",
    area: "Sicurezza Fisica",
    text: "Esistono sistemi di monitoraggio degli accessi (Es. videosorveglianza, allarmi anti-intrusione, ecc.)?"
  },
  {
    question_id: "19",
    area: "Sicurezza Fisica",
    text: "Nei locali che ospitano i server sono presenti sistemi di controllo contro gli agenti atmosferici (Es. rilevazione della temperatura, umidità o allagamento)?"
  },
  {
    question_id: "20",
    area: "Sicurezza Fisica",
    text: "È garantita la continuità dell'alimentazione elettrica tramite sistemi UPS e gruppi elettrogeni testati periodicamente?"
  },
  // Area: ICT (5.01 - 5.05)
  {
    question_id: "21",
    area: "ICT",
    text: "Viene applicata una gestione rigorosa degli accessi logici, con utenze individuali, privilegi minimi (Least Privilege) e autenticazione a più fattori (MFA) per gli amministratori?"
  },
  {
    question_id: "22",
    area: "ICT",
    text: "Viene eseguito un backup periodico dei dati, crittografato e conservato in sito remoto, con test di ripristino regolari?"
  },
  {
    question_id: "23",
    area: "ICT",
    text: "Vengono effettuati regolarmente Vulnerability Assessment e Penetration Test sui sistemi interni ed esposti all'esterno?"
  },
  {
    question_id: "24",
    area: "ICT",
    text: "Esiste un processo formalizzato per la gestione delle patch (aggiornamenti di sicurezza), con rilascio tempestivo di quelle critiche?"
  },
  {
    question_id: "25",
    area: "ICT",
    text: "La rete aziendale è segregata (es. tramite VLAN, DMZ e Firewall) per separare i sistemi critici dalle reti guest o meno sicure?"
  },
  // Area: Incidenti di Sicurezza (6.01 - 6.05)
  {
    question_id: "26",
    area: "Incidenti di Sicurezza",
    text: "Esiste una procedura o un piano dettagliato di risposta agli incidenti (Incident Response Plan) aggiornato periodicamente?"
  },
  {
    question_id: "27",
    area: "Incidenti di Sicurezza",
    text: "Gli incidenti ed i Data Breach vengono registrati, analizzati e classificati e le relative azioni documentate?"
  },
  {
    question_id: "28",
    area: "Incidenti di Sicurezza",
    text: "È garantita la notifica tempestiva degli incidenti rilevanti all'organizzazione committente e alle autorità competenti entro i tempi definiti?"
  },
  {
    question_id: "29",
    area: "Incidenti di Sicurezza",
    text: "Viene effettuata un'analisi delle cause (Root Cause Analysis) dopo ogni incidente per documentare le \"lezioni apprese\" e migliorare i controlli?"
  },
  {
    question_id: "30",
    area: "Incidenti di Sicurezza",
    text: "Vengono effettuati test di simulazione periodici per verificare l'efficacia delle procedure di risposta agli incidenti?"
  }
];

// Default tooltip texts for maturity levels (used as defaults for each question)
const DEFAULT_LEVEL_TOOLTIPS = {
  "0": "Seleziona questo livello se non esiste alcun processo o procedura per questa area. La sicurezza non è stata considerata o implementata.",
  "1": "Seleziona questo livello se esistono pratiche informali o occasionali, ma non documentate. La sicurezza dipende dall'iniziativa individuale.",
  "2": "Seleziona questo livello se esistono procedure documentate e applicate in modo consistente. La sicurezza è gestita in modo strutturato.",
  "3": "Seleziona questo livello se le procedure sono monitorate regolarmente con KPI e metriche. La sicurezza è misurata e controllata.",
  "4": "Seleziona questo livello se esiste un processo di miglioramento continuo. La sicurezza è ottimizzata costantemente basandosi sui dati."
};

// Generate questions template with predefined questions and default tooltips
const generateEmptyQuestions = () => {
  return PREDEFINED_QUESTIONS.map(q => ({
    ...q,
    tooltips: { ...DEFAULT_LEVEL_TOOLTIPS }
  }));
};

// Legacy - kept for backward compatibility
const DEFAULT_TOOLTIPS = [
  { level: 0, text: DEFAULT_LEVEL_TOOLTIPS["0"] },
  { level: 1, text: DEFAULT_LEVEL_TOOLTIPS["1"] },
  { level: 2, text: DEFAULT_LEVEL_TOOLTIPS["2"] },
  { level: 3, text: DEFAULT_LEVEL_TOOLTIPS["3"] },
  { level: 4, text: DEFAULT_LEVEL_TOOLTIPS["4"] }
];

// Colori come nello screenshot
const MATURITY_LEVELS = [
  { 
    value: 0, 
    label: "Non implementato", 
    description: "Il processo non è gestito: non sono definite modalità operative, non esistono responsabilità formalizzate e le attività sono svolte in modo estemporaneo da uno degli operatori, senza evidenza oggettiva delle sue competenze. Non esistono documenti o registrazioni a supporto del processo.",
    percentage: "0%", 
    bgColor: "#FEE2E2",
    borderColor: "#DC2626",
    textColor: "#DC2626"
  },
  { 
    value: 1, 
    label: "Carente", 
    subtitle: "(gestione informale)",
    description: "Il processo è gestito in maniera non formalizzata: sono presenti modalità operative di fatto, ma non sono formalizzate (manca una procedura o istruzione documentata). Le attività sono affidate a uno specifico operatore con evidenza delle sue competenze, ma ruoli e responsabilità non sono chiaramente definiti né condivisi; le registrazioni sono parziali o non sistematiche.",
    percentage: "25%", 
    bgColor: "#FEF3C7",
    borderColor: "#D97706",
    textColor: "#D97706"
  },
  { 
    value: 2, 
    label: "Accettabile", 
    subtitle: "(gestione formalizzata di base)",
    description: "Il processo è gestito in maniera formalizzata: esistono modalità operative definite e documentate (procedura/istruzioni), con ruoli e responsabilità chiaramente assegnati. Le attività sono svolte da operatori competenti, con evidenze oggettive delle competenze; sono presenti registrazioni essenziali, ma il riesame del processo è sporadico e il miglioramento è prevalentemente reattivo.",
    percentage: "55%", 
    bgColor: "#D1FAE5",
    borderColor: "#059669",
    textColor: "#059669"
  },
  { 
    value: 3, 
    label: "Buono", 
    subtitle: "(gestione formalizzata e monitorata)",
    description: "Il processo è pienamente formalizzato e applicato in modo coerente; ruoli e responsabilità sono definiti e comunicati, con adeguata copertura di competenze. Sono sistematicamente raccolti e analizzati dati e indicatori di performance del processo; vengono attuate azioni di miglioramento sulla base dei risultati, anche se non sempre in modo strutturato o integrato con altri processi.",
    percentage: "80%", 
    bgColor: "#DCFCE7",
    borderColor: "#16A34A",
    textColor: "#16A34A"
  },
  { 
    value: 4, 
    label: "Eccellente", 
    subtitle: "(gestione integrata e miglioramento continuo)",
    description: "Il processo è pienamente integrato nel sistema di gestione del fornitore, con chiara interazione con altri processi (es. qualità, rischio, supply chain). Gli indicatori sono stabili, monitorati con continuità e usati per pianificare miglioramenti proattivi; sono applicate logiche di miglioramento continuo (es. obiettivi periodici, progetti di ottimizzazione, benchmark) e l'organizzazione dimostra capacità di innovare il processo nel tempo.",
    percentage: "100%", 
    bgColor: "#D1FAE5",
    borderColor: "#059669",
    textColor: "#047857"
  },
];

const Questionnaire = () => {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [descriptions, setDescriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showScaleInfo, setShowScaleInfo] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const questionRefs = useRef({});
  const questionsPerPage = 5;
  
  // Helper function to check if user has admin privileges
  const isAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
  
  // Custom questions and tooltips state
  const [questions, setQuestions] = useState([]);
  const [customTooltips, setCustomTooltips] = useState(DEFAULT_TOOLTIPS);
  const [editingTooltip, setEditingTooltip] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(false);

  // Validate current page questions
  const validateCurrentPage = useCallback(() => {
    const startIdx = currentPage * questionsPerPage;
    const endIdx = (currentPage + 1) * questionsPerPage;
    const currentQuestions = questions.slice(startIdx, endIdx);
    
    const errors = {};
    let firstErrorId = null;
    
    for (const question of currentQuestions) {
      const qId = question.question_id || question.id;
      const answer = answers[qId];
      
      // Check if question is answered
      if (answer === undefined) {
        errors[qId] = { type: 'answer', message: 'Risposta obbligatoria' };
        if (!firstErrorId) firstErrorId = qId;
        continue;
      }
      
      // Check if description is required and filled (for levels >= 2)
      if (answer >= 2 && (!descriptions[qId] || descriptions[qId].trim() === '')) {
        errors[qId] = { type: 'description', message: 'Procedura obbligatoria per livello ≥ 2' };
        if (!firstErrorId) firstErrorId = qId;
      }
    }
    
    return { errors, firstErrorId };
  }, [currentPage, answers, descriptions, questionsPerPage, questions]);

  // Scroll and focus on error
  const scrollToError = useCallback((questionId) => {
    const element = questionRefs.current[questionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add highlight animation
      element.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
      }, 3000);
    }
  }, []);

  // Handle page navigation with validation
  const handleNextPage = useCallback(() => {
    const { errors, firstErrorId } = validateCurrentPage();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Count errors by type
      const unansweredCount = Object.values(errors).filter(e => e.type === 'answer').length;
      const missingProcedureCount = Object.values(errors).filter(e => e.type === 'description').length;
      
      let errorMessage = 'Completa la pagina prima di procedere:';
      if (unansweredCount > 0) {
        errorMessage += ` ${unansweredCount} domanda/e senza risposta`;
      }
      if (missingProcedureCount > 0) {
        errorMessage += `${unansweredCount > 0 ? ',' : ''} ${missingProcedureCount} procedura/e mancante/i`;
      }
      
      toast.error(errorMessage, {
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
        duration: 4000
      });
      
      if (firstErrorId) {
        setTimeout(() => scrollToError(firstErrorId), 100);
      }
      return false;
    }
    
    setValidationErrors({});
    setCurrentPage(p => Math.min(Math.ceil(questions.length / questionsPerPage) - 1, p + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return true;
  }, [validateCurrentPage, scrollToError, questionsPerPage, questions.length]);

  // Handle previous page (no validation needed)
  const handlePrevPage = useCallback(() => {
    setValidationErrors({});
    setCurrentPage(p => Math.max(0, p - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Check if questionnaire is complete (all questions answered + all procedures filled)
  const isQuestionnaireComplete = useMemo(() => {
    if (questions.length === 0) return false;
    
    // Check all questions have answers
    for (const question of questions) {
      const qId = question.question_id || question.id;
      const answer = answers[qId];
      
      // Question not answered
      if (answer === undefined) {
        return false;
      }
      
      // Answer >= 2 requires procedure
      if (answer >= 2 && (!descriptions[qId] || descriptions[qId].trim() === '')) {
        return false;
      }
    }
    return true;
  }, [answers, descriptions, questions]);

  // Get completion stats for UI feedback
  const completionStats = useMemo(() => {
    let answeredCount = 0;
    let proceduresRequired = 0;
    let proceduresFilled = 0;
    let questionsWithText = 0;

    for (const question of questions) {
      const qId = question.question_id || question.id;
      const answer = answers[qId];
      
      if (question.text && question.text.trim() !== '') {
        questionsWithText++;
      }
      
      if (answer !== undefined) {
        answeredCount++;
        
        if (answer >= 2) {
          proceduresRequired++;
          if (descriptions[qId] && descriptions[qId].trim() !== '') {
            proceduresFilled++;
          }
        }
      }
    }

    return {
      answeredCount,
      totalQuestions: questions.length,
      questionsWithText,
      proceduresRequired,
      proceduresFilled,
      missingAnswers: questions.length - answeredCount,
      missingProcedures: proceduresRequired - proceduresFilled
    };
  }, [answers, descriptions, questions]);

  // Export to Excel function
  const exportToExcel = useCallback(() => {
    const MATURITY_LABELS = {
      0: 'Non implementato (0%)',
      1: 'Carente (25%)',
      2: 'Accettabile (55%)',
      3: 'Buono (80%)',
      4: 'Eccellente (100%)'
    };

    // Prepare data rows
    const data = questions.map((question) => {
      const qId = question.question_id || question.id;
      const answer = answers[qId];
      const procedure = descriptions[qId] || '';
      const hasAnswer = answer !== undefined;
      const needsProcedure = hasAnswer && answer >= 2;
      const hasProcedure = procedure.trim() !== '';
      
      let status = 'Non compilato';
      if (hasAnswer) {
        if (needsProcedure) {
          status = hasProcedure ? 'Completo' : 'Procedura mancante';
        } else {
          status = 'Completo';
        }
      }
      
      return {
        'N.': qId,
        'Area': question.area,
        'Testo Domanda': question.text || '',
        'Risposta': hasAnswer ? answer : '',
        'Livello': hasAnswer ? MATURITY_LABELS[answer] : '',
        'Procedure': procedure,
        'Stato Compilazione': status
      };
    });

    // Add empty row and summary
    data.push({});
    data.push({ 'N.': 'Riepilogo' });
    data.push({ 'N.': 'Domande completate', 'Area': `${completionStats.answeredCount}/${completionStats.totalQuestions}` });
    data.push({ 'N.': 'Procedure richieste', 'Area': completionStats.proceduresRequired });
    data.push({ 'N.': 'Procedure compilate', 'Area': completionStats.proceduresFilled });
    data.push({ 'N.': 'Stato', 'Area': isQuestionnaireComplete ? 'Pronto per invio' : 'Compilazione incompleta' });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Questionario');

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // N.
      { wch: 20 },  // Area
      { wch: 60 },  // Testo Domanda
      { wch: 10 },  // Risposta
      { wch: 25 },  // Livello
      { wch: 40 },  // Procedure
      { wch: 18 }   // Stato
    ];

    // Generate and download file
    const filename = `Questionario_${questionnaire?.title || 'Export'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    
    toast.success('Questionario esportato in Excel');
  }, [answers, descriptions, completionStats, isQuestionnaireComplete, questionnaire, questions]);

  // Export Procedures/Documents to Excel
  const exportProcedures = useCallback(() => {
    // Filter only questions that have procedures filled
    const proceduresData = questions
      .map((question) => {
        const qId = question.question_id || question.id;
        const procedure = descriptions[qId] || '';
        const answer = answers[qId];
        
        if (procedure.trim() !== '') {
          return {
            'N.': qId,
            'Area': question.area,
            'Domanda': question.text || '',
            'Livello': answer !== undefined ? answer : '',
            'Procedura/Documento': procedure
          };
        }
        return null;
      })
      .filter(Boolean);

    if (proceduresData.length === 0) {
      toast.warning('Nessuna procedura/documento da esportare');
      return;
    }

    // Add summary row
    proceduresData.push({});
    proceduresData.push({ 'N.': 'Totale Procedure', 'Area': proceduresData.length - 1 });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(proceduresData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Procedure');

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // N.
      { wch: 25 },  // Area
      { wch: 50 },  // Domanda
      { wch: 8 },   // Livello
      { wch: 60 }   // Procedura/Documento
    ];

    // Generate and download file
    const officeName = questionnaire?.office_name || 'Ufficio';
    const filename = `Procedure_${officeName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    
    toast.success(`Esportate ${proceduresData.length - 2} procedure`);
  }, [answers, descriptions, questionnaire, questions]);

  // Import from Excel function
  const fileInputRef = useRef(null);
  
  const handleImportExcel = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Formato file non supportato. Usa Excel (.xlsx, .xls) o CSV');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let importedAnswers = 0;
        let importedProcedures = 0;
        const newAnswers = { ...answers };
        const newDescriptions = { ...descriptions };
        
        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // Parse Excel file
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          for (const row of jsonData) {
            const questionId = String(row['N.'] || '').trim();
            if (!questionId || questionId === 'Riepilogo') break;
            
            // Helper function to check if value is empty/invalid
            const isEmpty = (val) => {
              if (val === undefined || val === null || val === '') return true;
              if (typeof val === 'number' && isNaN(val)) return true;
              if (typeof val === 'string' && (val.trim() === '' || val.toLowerCase() === 'nan')) return true;
              return false;
            };
            
            // Try 'Risposta' column first, then fall back to 'Livello'
            let answerValue = row['Risposta'];
            if (isEmpty(answerValue)) {
              answerValue = row['Livello'];
            }
            const procedure = String(row['Procedure'] || row['Procedura'] || '').trim();
            
            // Validate question ID exists
            const questionExists = questions.find(q => (q.question_id || q.id).toString() === questionId);
            if (!questionExists) continue;
            
            // Parse answer value (0-4)
            if (!isEmpty(answerValue)) {
              const numAnswer = parseInt(answerValue, 10);
              if (!isNaN(numAnswer) && numAnswer >= 0 && numAnswer <= 4) {
                newAnswers[questionId] = numAnswer;
                importedAnswers++;
              }
            }
            
            // Import procedure if present
            if (procedure) {
              newDescriptions[questionId] = procedure;
              importedProcedures++;
            }
          }
        } else {
          // Parse CSV file
          const content = e.target.result;
          const lines = content.split('\n');
          
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line || line.startsWith('Riepilogo')) break;
            
            const parts = line.split(';');
            if (parts.length < 6) continue;
            
            const questionId = parts[0].trim();
            // Try 'Risposta' (index 3) first, then fall back to 'Livello' (index 4)
            let answerValue = parts[3]?.trim();
            if (!answerValue || answerValue === '') {
              answerValue = parts[4]?.trim();
            }
            const procedure = parts[5]?.trim().replace(/^"|"$/g, '') || '';
            
            const questionExists = questions.find(q => (q.question_id || q.id).toString() === questionId);
            if (!questionExists) continue;
            
            if (answerValue && answerValue !== '') {
              const numAnswer = parseInt(answerValue, 10);
              if (!isNaN(numAnswer) && numAnswer >= 0 && numAnswer <= 4) {
                newAnswers[questionId] = numAnswer;
                importedAnswers++;
              }
            }
            
            if (procedure) {
              newDescriptions[questionId] = procedure;
              importedProcedures++;
            }
          }
        }
        
        if (importedAnswers === 0) {
          toast.error('Nessuna risposta valida trovata nel file');
          return;
        }
        
        setAnswers(newAnswers);
        setDescriptions(newDescriptions);
        setValidationErrors({});
        
        toast.success(`Importate ${importedAnswers} risposte${importedProcedures > 0 ? ` e ${importedProcedures} procedure` : ''}`);
        
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Errore nella lettura del file. Verifica il formato.');
      }
    };
    
    reader.onerror = () => {
      toast.error('Errore nella lettura del file');
    };
    
    // Read as ArrayBuffer for Excel, as text for CSV
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file, 'UTF-8');
    }
    
    event.target.value = '';
  }, [answers, descriptions, questions]);

  useEffect(() => {
    if (id) {
      fetchQuestionnaire();
    }
  }, [id]);

  const fetchQuestionnaire = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/questionnaires/${id}`, { headers });
      setQuestionnaire(response.data);
      
      // Always use predefined questions with default tooltips
      setQuestions(generateEmptyQuestions());
      
      // Load custom tooltips (legacy - for backward compatibility)
      if (response.data.custom_tooltips && response.data.custom_tooltips.length > 0) {
        setCustomTooltips(response.data.custom_tooltips);
      } else {
        setCustomTooltips(DEFAULT_TOOLTIPS);
      }
      
      // Convert answers array to object for easier access
      const answersObj = {};
      const descriptionsObj = {};
      response.data.answers.forEach(a => {
        answersObj[a.question_id] = a.answer;
        if (a.description) {
          descriptionsObj[a.question_id] = a.description;
        }
      });
      setAnswers(answersObj);
      setDescriptions(descriptionsObj);
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      toast.error('Errore nel caricamento del questionario');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Handle question text change
  const handleQuestionTextChange = (questionId, newText) => {
    setQuestions(prev => prev.map(q => 
      (q.question_id || q.id) === questionId 
        ? { ...q, text: newText }
        : q
    ));
    setPendingChanges(true);
  };

  // Handle tooltip text change for a specific question and level
  const handleQuestionTooltipChange = (questionId, level, newText) => {
    setQuestions(prev => prev.map(q => {
      if ((q.question_id || q.id) === questionId) {
        const currentTooltips = q.tooltips || { ...DEFAULT_LEVEL_TOOLTIPS };
        return {
          ...q,
          tooltips: {
            ...currentTooltips,
            [String(level)]: newText
          }
        };
      }
      return q;
    }));
    setPendingChanges(true);
  };

  // Get tooltip text for a specific question and level
  const getQuestionTooltipText = (questionId, level) => {
    const question = questions.find(q => (q.question_id || q.id) === questionId);
    if (question?.tooltips && question.tooltips[String(level)]) {
      return question.tooltips[String(level)];
    }
    return DEFAULT_LEVEL_TOOLTIPS[String(level)] || '';
  };

  // Legacy - Handle global tooltip text change (deprecated)
  const handleTooltipChange = (level, newText) => {
    setCustomTooltips(prev => prev.map(t => 
      t.level === level ? { ...t, text: newText } : t
    ));
    setPendingChanges(true);
  };

  // Legacy - Get global tooltip text for a level (deprecated)
  const getTooltipText = (level) => {
    const tooltip = customTooltips.find(t => t.level === level);
    return tooltip?.text || DEFAULT_TOOLTIPS.find(t => t.level === level)?.text || '';
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: parseInt(value) }));
  };

  const handleDescriptionChange = (questionId, value) => {
    setDescriptions(prev => ({ ...prev, [questionId]: value }));
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const answersArray = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer,
        description: descriptions[question_id] || ""
      }));

      // Prepare custom questions for saving
      const customQuestionsData = questions.map(q => ({
        question_id: q.question_id || q.id,
        area: q.area,
        text: q.text || ""
      }));

      await axios.patch(
        `${API}/questionnaires/${id}`,
        { 
          answers: answersArray, 
          status: "draft",
          custom_questions: customQuestionsData,
          custom_tooltips: customTooltips
        },
        { headers }
      );
      setPendingChanges(false);
      toast.success('Bozza salvata');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Errore nel salvataggio della bozza');
    } finally {
      setSaving(false);
    }
  };

  const submitQuestionnaire = async () => {
    // Validate ALL questions, not just current page
    const allErrors = {};
    let firstErrorId = null;
    let firstErrorPage = null;
    
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const qId = question.question_id || question.id;
      const answer = answers[qId];
      const pageIdx = Math.floor(i / questionsPerPage);
      
      // Check if question is answered
      if (answer === undefined) {
        allErrors[qId] = { type: 'answer', message: 'Risposta obbligatoria' };
        if (firstErrorId === null) {
          firstErrorId = qId;
          firstErrorPage = pageIdx;
        }
        continue;
      }
      
      // Check if description is required and filled (for levels >= 2)
      if (answer >= 2 && (!descriptions[qId] || descriptions[qId].trim() === '')) {
        allErrors[qId] = { type: 'description', message: 'Procedura obbligatoria per livello ≥ 2' };
        if (firstErrorId === null) {
          firstErrorId = qId;
          firstErrorPage = pageIdx;
        }
      }
    }
    
    if (Object.keys(allErrors).length > 0) {
      const unansweredCount = Object.values(allErrors).filter(e => e.type === 'answer').length;
      const missingProcedureCount = Object.values(allErrors).filter(e => e.type === 'description').length;
      
      let errorMessage = 'Completa tutte le domande:';
      if (unansweredCount > 0) {
        errorMessage += ` ${unansweredCount} domanda/e senza risposta`;
      }
      if (missingProcedureCount > 0) {
        errorMessage += `${unansweredCount > 0 ? ',' : ''} ${missingProcedureCount} procedura/e mancante/i`;
      }
      
      toast.error(errorMessage, {
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
        duration: 4000
      });
      
      // Navigate to the page with first error
      if (firstErrorPage !== null && firstErrorPage !== currentPage) {
        setCurrentPage(firstErrorPage);
        // Set validation errors for that page
        const pageErrors = {};
        const startIdx = firstErrorPage * questionsPerPage;
        const endIdx = (firstErrorPage + 1) * questionsPerPage;
        questions.slice(startIdx, endIdx).forEach(q => {
          const qId = q.question_id || q.id;
          if (allErrors[qId]) {
            pageErrors[qId] = allErrors[qId];
          }
        });
        setValidationErrors(pageErrors);
        setTimeout(() => scrollToError(firstErrorId), 300);
      } else {
        // Same page - just highlight errors
        const pageErrors = {};
        const startIdx = currentPage * questionsPerPage;
        const endIdx = (currentPage + 1) * questionsPerPage;
        questions.slice(startIdx, endIdx).forEach(q => {
          const qId = q.question_id || q.id;
          if (allErrors[qId]) {
            pageErrors[qId] = allErrors[qId];
          }
        });
        setValidationErrors(pageErrors);
        if (firstErrorId) {
          setTimeout(() => scrollToError(firstErrorId), 100);
        }
      }
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const answersArray = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer,
        description: descriptions[question_id] || ""
      }));

      // Prepare custom questions for saving
      const customQuestionsData = questions.map(q => ({
        question_id: q.question_id || q.id,
        area: q.area,
        text: q.text || ""
      }));

      await axios.patch(
        `${API}/questionnaires/${id}`,
        { 
          answers: answersArray, 
          status: "completed",
          custom_questions: customQuestionsData,
          custom_tooltips: customTooltips
        },
        { headers }
      );
      toast.success('Questionario completato!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      toast.error('Errore nell\'invio del questionario');
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = async () => {
    if (questionnaire?.status !== 'completed') {
      toast.error('Il questionario deve essere completato prima di esportarlo');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/questionnaires/${id}/export-pdf`, {
        headers,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Report_${questionnaire.title}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF scaricato con successo');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Errore nell\'export del PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-[#3BA99C]" />
      </div>
    );
  }

  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-[#3BA99C] transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Torna alla Dashboard
        </button>
      </div>

      {/* Scala di Maturità Info */}
      <Card className="bg-slate-50 border-slate-200">
        <CardHeader 
          className="cursor-pointer py-3"
          onClick={() => setShowScaleInfo(!showScaleInfo)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="w-5 h-5 text-[#3BA99C]" />
              Scala di Maturità (0-4)
            </CardTitle>
            {showScaleInfo ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            La scala di maturità dei processi fornitore è articolata su 5 livelli (da 0 a 4), ciascuno con un valore percentuale che contribuisce al punteggio finale.
          </p>
        </CardHeader>
        {showScaleInfo && (
          <CardContent className="pt-0 space-y-3">
            {MATURITY_LEVELS.map((level) => (
              <div
                key={level.value}
                className="p-4 rounded-lg border-l-4"
                style={{ 
                  borderLeftColor: level.borderColor,
                  backgroundColor: level.bgColor
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: level.borderColor }}
                    >
                      {level.value}
                    </span>
                    <span className="font-semibold" style={{ color: level.textColor }}>
                      {level.label} {level.subtitle && <span className="font-normal">{level.subtitle}</span>}
                    </span>
                  </div>
                  <span className="text-sm font-bold" style={{ color: level.textColor }}>
                    {level.percentage}
                  </span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {level.description}
                </p>
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      {/* Header con titolo, progresso e pulsanti export/import */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-2xl">{questionnaire?.title}</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                {answeredCount}/{questions.length} domande completate
              </p>
            </div>
            
            {/* Export/Import buttons in alto a destra */}
            <div className="flex gap-2 flex-wrap">
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={exportProcedures}
                      className="flex items-center gap-2 px-3 py-2 bg-white border border-amber-600 text-amber-600 rounded-lg hover:bg-amber-50 transition-colors text-sm"
                      data-testid="export-procedures-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Procedure
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    Esporta l'elenco dei documenti e delle procedure citate nelle risposte del questionario
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={exportToExcel}
                      className="flex items-center gap-2 px-3 py-2 bg-white border border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors text-sm"
                      data-testid="export-excel-btn"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Esporta
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Scarica le risposte in formato Excel
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {questionnaire?.status !== 'completed' && !isAdminRole && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImportExcel}
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    data-testid="import-excel-input"
                  />
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                          data-testid="import-excel-btn"
                        >
                          <Upload className="w-4 h-4" />
                          Importa
                          <HelpCircle className="w-3 h-3 opacity-60" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="bottom" 
                        className="max-w-sm p-3 bg-slate-900 text-white border-0 shadow-xl"
                        sideOffset={8}
                      >
                        <div className="space-y-2">
                          <p className="font-semibold text-sm">Formato file supportato (.xlsx, .csv):</p>
                          <ul className="text-xs space-y-1 opacity-90">
                            <li>• <strong>N.</strong> - Numero domanda (1-30)</li>
                            <li>• <strong>Livello</strong> - Valore 0-4</li>
                            <li>• <strong>Procedure</strong> - Testo (obbligatorio se Livello ≥ 2)</li>
                          </ul>
                          <p className="text-xs text-blue-300 mt-2">💡 Usa "Esporta" per ottenere un file template</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              
              {questionnaire?.status === 'completed' && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={exportPDF}
                        className="flex items-center gap-2 px-3 py-2 bg-[#21358E] text-white rounded-lg hover:bg-[#1a2a70] transition-colors text-sm"
                        data-testid="export-pdf-btn"
                      >
                        <FileDown className="w-4 h-4" />
                        Report Completo PDF
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      Report di valutazione completo con tutte le risposte
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {/* Progress bar totale */}
          <div className="mt-4">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-[#3BA99C] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Area Progress Tabs - Integrato */}
          <div className="mt-4 flex flex-wrap gap-2">
            {DEFAULT_AREAS.map((area, index) => {
              const areaQuestions = questions.filter(q => q.area === area);
              const areaAnswered = areaQuestions.filter(q => answers[q.question_id || q.id] !== undefined).length;
              const isCurrentArea = currentPage === index;
              const isComplete = areaAnswered === areaQuestions.length;
              
              // Check if previous areas are complete (to enable/disable navigation)
              const previousAreasComplete = DEFAULT_AREAS.slice(0, index).every((prevArea) => {
                const prevQuestions = questions.filter(q => q.area === prevArea);
                const prevAnswered = prevQuestions.filter(q => answers[q.question_id || q.id] !== undefined).length;
                return prevAnswered === prevQuestions.length;
              });
              
              const isLocked = index > 0 && !previousAreasComplete && !isComplete;
              
              return (
                <button
                  key={area}
                  onClick={() => {
                    if (!isLocked || isAdminRole) {
                      setCurrentPage(index);
                    } else {
                      toast.warning('Completa prima le aree precedenti');
                    }
                  }}
                  disabled={isLocked && !isAdminRole}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isCurrentArea 
                      ? 'bg-[#3BA99C] text-white' 
                      : isComplete
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : isLocked
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`area-tab-${index}`}
                >
                  {isLocked && <Lock className="w-3 h-3" />}
                  <span className="hidden sm:inline">{area}</span>
                  <span className="sm:hidden">{area.split(' ')[0]}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    isCurrentArea 
                      ? 'bg-white/20' 
                      : isComplete 
                        ? 'bg-green-200 text-green-800'
                        : 'bg-slate-200'
                  }`}>
                    {areaAnswered}/{areaQuestions.length}
                  </span>
                </button>
              );
            })}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-8 border-t border-slate-100 pt-6">
          {currentQuestions.map((question) => {
            const qId = question.question_id || question.id;
            const hasError = validationErrors[qId];
            const errorType = hasError?.type;
            const canSelectAnswer = !isAdminRole && questionnaire?.status !== 'completed';
            
            return (
              <div 
                key={qId} 
                ref={el => questionRefs.current[qId] = el}
                className={`space-y-4 p-4 rounded-lg transition-all duration-300 ${hasError ? 'bg-red-50 border border-red-200' : ''}`}
                data-testid={`question-${qId}`}
              >
                <div className="flex gap-3">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full text-white flex items-center justify-center font-semibold text-sm ${hasError && errorType === 'answer' ? 'bg-red-500' : 'bg-[#3BA99C]'}`}>
                    {qId}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 mb-1">{question.text || 'Domanda non configurata'}</p>
                    <p className="text-xs text-slate-500">Area: {question.area}</p>
                    {hasError && errorType === 'answer' && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {hasError.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Maturity Level Selection */}
                <div className="space-y-2 pl-11">
                  <TooltipProvider delayDuration={300}>
                  {MATURITY_LEVELS.map((level) => {
                    const isSelected = answers[qId] === level.value;
                    const canSelect = canSelectAnswer;
                    return (
                      <Tooltip key={level.value}>
                        <TooltipTrigger asChild>
                          <div
                            onClick={() => {
                              if (!canSelect) return;
                              handleAnswerChange(qId, level.value);
                              // Clear error when user answers
                              if (validationErrors[qId]?.type === 'answer') {
                                setValidationErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors[qId];
                                  return newErrors;
                                });
                              }
                            }}
                            className={`flex items-center gap-3 p-3 rounded-lg transition-all border-l-4 ${canSelect ? 'cursor-pointer' : 'cursor-default'}`}
                            style={{
                              borderLeftColor: isSelected ? level.borderColor : '#E2E8F0',
                              backgroundColor: isSelected ? level.bgColor : 'white',
                              border: isSelected ? `1px solid ${level.borderColor}` : '1px solid #E2E8F0',
                              borderLeftWidth: '4px'
                            }}
                          >
                            <span 
                              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                              style={{ 
                                backgroundColor: isSelected ? level.borderColor : '#E2E8F0',
                                color: isSelected ? 'white' : '#64748B'
                              }}
                            >
                              {level.value}
                            </span>
                            <div className="flex-1">
                              <span className="font-medium text-slate-900">
                                {level.label} {level.subtitle && <span className="font-normal text-slate-500">{level.subtitle}</span>}
                              </span>
                            </div>
                            <HelpCircle className="w-4 h-4 text-slate-400 opacity-50 hover:opacity-100 transition-opacity" />
                            <span 
                              className="text-sm font-bold"
                              style={{ color: level.textColor }}
                            >
                              {level.percentage}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="top" 
                          align="center"
                          className="max-w-md p-4 bg-slate-900/95 backdrop-blur-sm text-white border-0 shadow-xl z-50"
                          sideOffset={8}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span 
                                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                                style={{ backgroundColor: level.borderColor, color: 'white' }}
                              >
                                {level.value}
                              </span>
                              <span className="font-semibold">
                                {level.label} {level.subtitle && <span className="font-normal opacity-80">{level.subtitle}</span>}
                              </span>
                              <span className="ml-auto font-bold" style={{ color: level.borderColor }}>
                                {level.percentage}
                              </span>
                            </div>
                            <p className="text-sm leading-relaxed opacity-90">
                              {getQuestionTooltipText(qId, level.value)}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                  </TooltipProvider>
                </div>

                {/* Description Field - Only for levels >= 2, mandatory */}
                {answers[qId] !== undefined && answers[qId] >= 2 && (
                  <div className="pl-11 animate-in slide-in-from-top-2 duration-200">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Elenco delle procedure <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={descriptions[qId] || ''}
                      onChange={(e) => {
                        handleDescriptionChange(qId, e.target.value);
                        // Clear error when user types
                        if (validationErrors[qId]?.type === 'description' && e.target.value.trim()) {
                          setValidationErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[qId];
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="Inserisci il nome della procedura, documento o misura adottata..."
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3BA99C] focus:border-transparent resize-none ${hasError && errorType === 'description' ? 'border-red-500 bg-red-50' : 'border-slate-200'} ${(questionnaire?.status === 'completed' || isAdminRole) ? 'bg-slate-50' : ''}`}
                      rows={2}
                      disabled={questionnaire?.status === 'completed' || isAdminRole}
                      required
                    />
                    {hasError && errorType === 'description' && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {hasError.message}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Completion Status Bar - Only for non-admin users */}
      {questionnaire?.status !== 'completed' && !isAdminRole && (
        <Card className={`border-l-4 ${isQuestionnaireComplete ? 'border-l-green-500 bg-green-50' : 'border-l-amber-500 bg-amber-50'}`}>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isQuestionnaireComplete ? (
                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-white" />
                  </div>
                )}
                <div>
                  <p className={`font-medium ${isQuestionnaireComplete ? 'text-green-700' : 'text-amber-700'}`}>
                    {isQuestionnaireComplete ? 'Questionario pronto per l\'invio' : 'Compilazione incompleta'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {completionStats.answeredCount}/{completionStats.totalQuestions} domande 
                    {completionStats.proceduresRequired > 0 && (
                      <> • {completionStats.proceduresFilled}/{completionStats.proceduresRequired} procedure</>
                    )}
                  </p>
                </div>
              </div>
              {!isQuestionnaireComplete && (
                <div className="text-sm text-slate-600">
                  {completionStats.missingAnswers > 0 && (
                    <span className="block">{completionStats.missingAnswers} risposte mancanti</span>
                  )}
                  {completionStats.missingProcedures > 0 && (
                    <span className="block">{completionStats.missingProcedures} procedure mancanti</span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation & Actions */}
      <div className="flex flex-col gap-4">
        {/* Navigation row with centered actions */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Previous Area */}
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
            data-testid="prev-page-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Area Precedente
          </button>

          {/* Center: Salva Bozza + Aiuto */}
          <div className="flex gap-2 flex-wrap justify-center">
            {questionnaire?.status !== 'completed' && !isAdminRole && (
              <button
                onClick={saveDraft}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-[#3BA99C] text-[#3BA99C] rounded-lg hover:bg-[#3BA99C]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-draft-btn"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Salva Bozza
              </button>
            )}
            
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href="mailto:hd@etom.it?subject=Richiesta%20Supporto%20e-CAA-CONFAGRICOLTURA"
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50"
                    data-testid="help-btn"
                  >
                    <HelpCircle className="w-5 h-5" />
                    Aiuto
                  </a>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  Invia email a hd@etom.it
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Right: Next Area */}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-end"
            data-testid="next-page-btn"
          >
            Area Successiva
            <ArrowLeft className="w-4 h-4 rotate-180" />
          </button>
        </div>

        {/* Submit button row - centered below */}
        {questionnaire?.status !== 'completed' && !isAdminRole && (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={submitQuestionnaire}
              disabled={saving || !isQuestionnaireComplete}
              className={`flex items-center gap-2 px-7 py-2.5 text-base font-medium rounded-lg transition-colors ${
                isQuestionnaireComplete 
                  ? 'bg-[#3BA99C] text-white hover:bg-[#2D8A7F]' 
                  : 'bg-slate-200 text-slate-500 cursor-not-allowed border border-slate-300'
              }`}
              data-testid="submit-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Invia Questionario
            </button>
            <p className="text-xs text-slate-500 text-center max-w-md">
              Ti ricordiamo che il trattamento dei dati avviene nel rispetto della{' '}
              <a href="/privacy-policy" target="_blank" className="text-[#3BA99C] hover:underline">Privacy Policy</a>
              {' '}e dei{' '}
              <a href="/termini-condizioni" target="_blank" className="text-[#3BA99C] hover:underline">Termini e Condizioni</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Questionnaire;
